import React, {useState} from 'react';
import {FlatList} from 'react-native';
import NoResults from 'src/common-components/noResults';
import {compose} from 'recompose';
import {withObservables} from '@nozbe/watermelondb/react';
import {withDatabase} from '@nozbe/watermelondb/react';
import {useDatabase} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import {useStyle} from 'src/providers/style';
import withState from 'src/redux/wrapper';
import {fuzzy} from 'src/common-utils/fuzzy';
import {of} from 'rxjs';
import DeleteModal from 'src/design-system/delete-modal';
import {useTranslations} from 'src/providers/translation';
import {BaseIndexScreen, Modal} from 'src/design-system';
import {Searchbar} from 'react-native-paper';
import {useSelector} from 'react-redux';
import PhysicianListItem from '../physician-list-item';
import {Credential, Patient, PayerCredential, Role} from 'src/models';
import PhysicianForm from '../physician-form';

interface Props {
  physicians: any[];
  role: Role;
}

const PhysicianTab = ({physicians, role}: Props) => {
  const database = useDatabase();
  const translations = useTranslations();
  const styles = useStyle();
  const {selectedGroup, userId} = useSelector(state => state.authentication);

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [show, setShow] = useState(false);
  const [showDelete, setShowDelete] = useState<string | boolean>(false);
  const [selectedPhysician, setPhysician] = useState(null);

  const title = selectedPhysician ? 'Edit Physician' : 'Add Physician';
  const searchedItems = physicians?.filter((physician: any) => {
    return fuzzy(physician?.firstName, searchFilter);
  });

  const addCallback = () => {
    setPhysician(null);
    setShow(true);
  };
  const onSubmit = async (values: any) => {
    if (selectedPhysician) {
      await selectedPhysician.updateEntity({
        tin: values.referring.tin,
        npi: values.referring.npi,
        taxonomy: values.referring.taxonomy,
        firstName: values.referring.firstName,
        middleName: values.referring.middleName,
        lastName: values.referring.lastName,
      });
      for (const payer of values.referring.payers) {
        if (payer._id) {
          const payerCredential = await database
            .get(PayerCredential.table)
            .find(payer._id);
          payerCredential.updateEntity({
            legacyId: payer.legacyId,
            taxonomy: payer.taxonomy,
          });
        } else if (payer.legacyId !== '') {
          await database?.write(async () => {
            return database?.get(PayerCredential.table).create(entity => {
              entity.partition = selectedGroup;
              entity.credentialId = selectedPhysician.id;
              entity.legacyId = payer.legacyId;
              entity.payerId = payer.id;
              entity.taxonomy = payer.taxonomy;
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          });
        }
      }
    } else if (values.referring) {
      await database?.write(async () => {
        return database?.get(Credential.table).create(entity => {
          entity.partition = selectedGroup;
          entity.tin = values.referring.tin;
          entity.npi = values.referring.npi;
          entity.taxonomy = values.referring.taxonomy;
          entity.firstName = values.referring.firstName;
          entity.middleName = values.referring.middleName;
          entity.lastName = values.referring.lastName;
          entity.createdBy = userId;
          entity.updatedBy = userId;
        });
      });
    }
    setShow(false);
  };

  const deleteCancelled = () => {
    setShowDelete(false);
  };
  const deleteConfirmed = async (item: any) => {
    item.delete();
    const patients = await database
      .get(Patient.table)
      .query(Q.where('referring_id', item?.id));
    for (const patient of patients) {
      await patient.updateEntity({
        referringId: '',
      });
    }
    setShowDelete(false);
  };
  const editCallBack = (item: any) => {
    setPhysician(item);
    setShow(true);
  };
  return (
    <BaseIndexScreen
      title={'Physicians'}
      search={
        <Searchbar
          value={searchFilter}
          onChangeText={setSearchFilter}
          placeholder={translations('search')}
        />
      }
      add={addCallback}
      canAdd={role?.organizationLocationCreate || true}>
      <DeleteModal
        model={'Physician'}
        text={
          showDelete ? `${showDelete?.firstName} ${showDelete?.lastName}` : null
        }
        show={[showDelete, setShowDelete]}
        cancelled={deleteCancelled}
        handleSubmit={() => deleteConfirmed(showDelete)}
      />
      <Modal show={[show, setShow]} title={title}>
        <PhysicianForm
          physician={selectedPhysician}
          selectedGroup={selectedGroup}
          onAssign={onSubmit}
        />
      </Modal>
      {searchedItems?.length === 0 ? (
        <NoResults
          iconName="account-supervisor"
          message={'No Physicians Found'}
        />
      ) : (
        <FlatList
          data={searchedItems}
          scrollEnabled={true}
          keyExtractor={item => item?.id?.toString()}
          renderItem={({item}) => {
            return (
              <PhysicianListItem
                physician={item}
                canDelete={role?.organizationLocationDelete || true}
                canEdit={role?.organizationLocationEdit || true}
                deleteCallback={setShowDelete}
                editCallBack={editCallBack}
              />
            );
          }}
          style={[styles.flex]}
        />
      )}
    </BaseIndexScreen>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['authentication'], ({database, authentication}: any) => ({
    profile: authentication.userId
      ? database.get('users').findAndObserve(authentication.userId)
      : of({}),
  })),
  withObservables([], ({profile}: any) => {
    return {
      role: profile.role,
    };
  }),
  withObservables([''], ({database, authentication}: any) => ({
    physicians: database
      .get('credentials')
      .query(
        Q.where('deleted_at', Q.eq(null)),
        Q.where('_partition', authentication.selectedGroup),
        Q.where('user_id', ''),
      ),
  })),
)(PhysicianTab);
